import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import type { Location } from 'react-router';

import type { TilePermissionResolvers } from './availability/types';

export const URL_TILE_SEARCH_PARAM = 'tile';

export type UrlTilePayload = {
    readonly [key: string]: unknown;
    readonly type: string;
};

export type UrlTileMutationOptions = {
    readonly replace?: boolean;
};

export type UrlTileHost = 'main' | 'panel';

export type UrlTileRenderContext = {
    readonly closeTile: (slotKey: string, options?: UrlTileMutationOptions) => void;
    readonly host?: UrlTileHost;
    readonly location: Location;
    readonly openTile: (payload: UrlTilePayload, options?: UrlTileMutationOptions) => void;
    readonly updateTile: (payload: UrlTilePayload, options?: UrlTileMutationOptions) => void;
};

export type UrlTileWidths = {
    readonly maxWidth?: string;
    readonly minWidth?: string;
    readonly width?: string;
};

export type UrlTileWidthInput<TPayload> = string | ((payload: TPayload) => string);

export type UrlTileCommentsGroupParent = {
    readonly groupKey: (payload: UrlTilePayload) => string | undefined;
    readonly isCommentsOpen: (payload: UrlTilePayload) => boolean;
    readonly role: 'parent';
};

export type UrlTileCommentsGroupChild = {
    readonly groupKey: (payload: UrlTilePayload) => string | undefined;
    // Present when the child can host the group's comment tile itself while its parent tile
    // is not open, e.g. a task deep-linked without its profile.
    readonly isCommentsOpen?: (payload: UrlTilePayload) => boolean;
    readonly parentType: string;
    readonly role: 'child';
};

export type UrlTileCommentsGroup = UrlTileCommentsGroupChild | UrlTileCommentsGroupParent;

export type UrlTileCommentsGroupInput<TPayload> =
    | {
          readonly groupKey: (payload: TPayload) => string;
          readonly isCommentsOpen: (payload: TPayload) => boolean;
          readonly role: 'parent';
      }
    | {
          readonly groupKey: (payload: TPayload) => string;
          readonly isCommentsOpen?: (payload: TPayload) => boolean;
          readonly parentType: string;
          readonly role: 'child';
      };

export type UrlTileDefinition = {
    readonly commentsGroup?: UrlTileCommentsGroup;
    readonly holdAutoScroll?: boolean;
    readonly parseValue: (value: string) => UrlTilePayload | undefined;
    readonly renderPayload: (payload: UrlTilePayload, context: UrlTileRenderContext) => ReactNode | undefined;
    readonly serializePayload: (payload: UrlTilePayload) => string | undefined;
    readonly slotKeyForPayload: (payload: UrlTilePayload) => string | undefined;
    readonly tileKeyForPayload: (payload: UrlTilePayload) => string | undefined;
    readonly type: string;
    readonly widthsForPayload: (payload: UrlTilePayload) => UrlTileWidths;
    readonly zIndex?: number;
};

// Owns the URL value encoding of one tile type: parse and serialize are generated
// from one declaration, so they cannot drift apart. Created via createUrlTileCodec.
export type UrlTileCodec<TPayload extends UrlTilePayload> = {
    readonly parseTokens: (tokens: readonly string[]) => unknown;
    readonly serialize: (payload: TPayload) => string;
    readonly type: TPayload['type'];
};

export type UrlTileDefinitionInput<TPayload extends UrlTilePayload, TProps> = {
    readonly codec: UrlTileCodec<TPayload>;
    readonly commentsGroup?: UrlTileCommentsGroupInput<TPayload>;
    readonly holdAutoScroll?: boolean;
    readonly maxWidth?: UrlTileWidthInput<TPayload>;
    readonly minWidth?: UrlTileWidthInput<TPayload>;
    readonly render: (props: TProps) => ReactNode;
    readonly schema: {
        readonly safeParse: (value: unknown) =>
            | {
                  readonly data: TPayload;
                  readonly success: true;
              }
            | {
                  readonly success: false;
              };
    };
    readonly slotKey: (payload: TPayload) => string;
    readonly tileKey: (payload: TPayload) => string;
    readonly toProps: (payload: TPayload, context: UrlTileRenderContext) => TProps;
    readonly width?: UrlTileWidthInput<TPayload>;
    readonly zIndex?: number;
};

export type ParsedUrlTile = {
    readonly definition: UrlTileDefinition;
    readonly payload: UrlTilePayload;
    readonly slotKey: string;
    readonly tileKey: string;
    readonly value: string;
};

export type UrlTileLegacyRewriteParams = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly hadCanonicalTileParams: boolean;
    readonly location: Location;
    readonly searchParams: URLSearchParams;
};

export type UrlTileChromeRenderer = ({
    content,
    context,
    tile,
}: {
    readonly content: ReactNode;
    readonly context: UrlTileRenderContext;
    readonly tile: ParsedUrlTile;
}) => ReactNode;

export type UrlCommentTileChromeRenderer = (content: ReactNode) => ReactNode;

export type UrlTileLegacyRewriter = {
    readonly rewrite: (params: UrlTileLegacyRewriteParams) => URLSearchParams | undefined;
};

export type UrlTileModule = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly legacyRewriters?: readonly UrlTileLegacyRewriter[];
    readonly permissionResolvers?: TilePermissionResolvers;
    readonly Provider?: FunctionComponent<PropsWithChildren>;
    readonly renderTileChrome?: UrlTileChromeRenderer;
};
